import React from 'react';
import { QuoteStatus } from '../../types';
import { QuotePeriod, QuoteStats } from '../../services/quoteStats';
import { Euro, Bell, PenLine, Target, TrendingUp, CalendarX } from 'lucide-react';

// Kachelreihe mit den Angebots-Kennzahlen. Rein praesentational: rechnen tut
// quoteStats.ts, hier wird nur formatiert. Wird an zwei Stellen benutzt –
// klickbar in der Angebotsliste (setzt den Statusfilter) und statisch im
// Finanz-Cockpit.

const eur0 = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
});

const fmtEur = (n: number) => eur0.format(Number.isFinite(n) ? n : 0);

/** null (kein Nenner) wird zu "—", nie zu "0 %" – das waere eine andere Aussage. */
const fmtPct = (v: number | null) => (v === null ? '—' : `${Math.round(v)} %`);

const PERIODS: { key: QuotePeriod; label: string }[] = [
    { key: '90d', label: '90 Tage' },
    { key: 'year', label: 'Jahr' },
    { key: 'all', label: 'Alle' },
];

const PERIOD_SHORT: Record<QuotePeriod, string> = {
    '90d': '90 T',
    year: 'Jahr',
    all: 'gesamt',
};

type Tone = 'blue' | 'amber' | 'slate' | 'emerald' | 'red';

const TONE: Record<Tone, { icon: string; value: string; ring: string }> = {
    blue: { icon: 'bg-blue-50 text-blue-600', value: 'text-slate-800', ring: 'hover:border-blue-300' },
    amber: { icon: 'bg-amber-50 text-amber-600', value: 'text-slate-800', ring: 'hover:border-amber-300' },
    slate: { icon: 'bg-slate-100 text-slate-500', value: 'text-slate-800', ring: 'hover:border-slate-300' },
    emerald: { icon: 'bg-emerald-50 text-emerald-600', value: 'text-slate-800', ring: 'hover:border-emerald-300' },
    red: { icon: 'bg-red-50 text-red-600', value: 'text-red-700', ring: 'hover:border-red-300' },
};

interface TileProps {
    caption: string;
    /** Kennzeichnet Kacheln, die sich auf das Zeitfenster beziehen statt auf heute. */
    badge?: string;
    value: string;
    sub: string;
    icon: React.ReactNode;
    tone: Tone;
    onClick?: () => void;
}

const Tile: React.FC<TileProps> = ({ caption, badge, value, sub, icon, tone, onClick }) => {
    const t = TONE[tone];
    const cls = `text-left w-full bg-white border border-slate-200 rounded-2xl px-4 py-3 shadow-sm transition-all ${
        onClick ? `cursor-pointer hover:shadow-md ${t.ring}` : ''
    }`;
    const body = (
        <>
            <div className="flex items-start justify-between gap-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 leading-tight">
                    {caption}
                    {badge && <span className="ml-1 normal-case tracking-normal text-slate-300">· {badge}</span>}
                </span>
                <span className={`shrink-0 p-1.5 rounded-lg ${t.icon}`}>{icon}</span>
            </div>
            <p className={`mt-2 text-xl font-black tabular-nums leading-none ${t.value}`}>{value}</p>
            <p className="mt-1 text-[11px] text-slate-400 truncate">{sub}</p>
        </>
    );

    return onClick ? (
        <button type="button" onClick={onClick} data-glass="kachel" className={cls}>
            {body}
        </button>
    ) : (
        <div data-glass="kachel" className={cls}>{body}</div>
    );
};

interface QuoteKpiBarProps {
    stats: QuoteStats;
    /** Nur noetig fuer die eigenen Zeitraum-Pills. */
    period?: QuotePeriod;
    /** Ueberschreibt die Zeitraum-Beschriftung – fuer Aufrufer mit eigenem Umschalter. */
    periodLabel?: string;
    /** Weglassen, wenn der Zeitraum von aussen gesteuert wird (Finanz-Cockpit). */
    onPeriodChange?: (p: QuotePeriod) => void;
    /** Weglassen macht die Kacheln nicht klickbar. */
    onSelectStatus?: (s: QuoteStatus | 'all') => void;
    onFollowUpClick?: () => void;
}

export const QuoteKpiBar: React.FC<QuoteKpiBarProps> = ({
    stats,
    period,
    periodLabel,
    onPeriodChange,
    onSelectStatus,
    onFollowUpClick,
}) => {
    const p = periodLabel ?? (period ? PERIOD_SHORT[period] : undefined);
    const due = stats.dueFollowUpCount;

    return (
        <div data-glass="kennzahlen" className="space-y-2">
            {onPeriodChange && period && (
                <div className="flex items-center justify-end gap-1">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mr-1">Zeitraum</span>
                    <div data-glass="umschalter" className="flex gap-1 bg-slate-100 p-1 rounded-xl">
                        {PERIODS.map((opt) => (
                            <button
                                key={opt.key}
                                type="button"
                                onClick={() => onPeriodChange(opt.key)}
                                className={`px-3 py-1 rounded-lg text-[11px] font-bold transition-all ${
                                    period === opt.key
                                        ? 'bg-white shadow-sm text-blue-600'
                                        : 'text-slate-400 hover:text-slate-600'
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <Tile
                    caption="Offene Pipeline"
                    value={fmtEur(stats.openNet)}
                    sub={`${stats.openCount} ${stats.openCount === 1 ? 'Angebot' : 'Angebote'} zur Entscheidung`}
                    icon={<Euro className="w-4 h-4" />}
                    tone="blue"
                    onClick={onSelectStatus ? () => onSelectStatus('sent') : undefined}
                />
                <Tile
                    caption="Nachfassen fällig"
                    value={String(due)}
                    sub={due > 0 ? 'jetzt hinterhertelefonieren' : 'nichts offen'}
                    icon={<Bell className="w-4 h-4" />}
                    tone={due > 0 ? 'red' : 'slate'}
                    onClick={due > 0 ? onFollowUpClick : undefined}
                />
                <Tile
                    caption="Entwürfe"
                    value={String(stats.draftCount)}
                    sub={
                        stats.oldestDraftDays === null
                            ? 'keine offenen Entwürfe'
                            : `ältester seit ${stats.oldestDraftDays} ${stats.oldestDraftDays === 1 ? 'Tag' : 'Tagen'}`
                    }
                    icon={<PenLine className="w-4 h-4" />}
                    tone={stats.oldestDraftDays !== null && stats.oldestDraftDays > 30 ? 'amber' : 'slate'}
                    onClick={onSelectStatus ? () => onSelectStatus('draft') : undefined}
                />
                <Tile
                    caption="Erfolgsquote"
                    badge={p}
                    value={fmtPct(stats.winRatePct)}
                    sub={
                        stats.decidedCount === 0
                            ? 'nichts entschieden'
                            : `${stats.wonCount} von ${stats.decidedCount} · nach Wert`
                    }
                    icon={<Target className="w-4 h-4" />}
                    tone="emerald"
                />
                <Tile
                    caption="Gewonnen"
                    badge={p}
                    value={fmtEur(stats.wonNet)}
                    sub={stats.avgWonNet === null ? 'noch kein Auftrag' : `Ø ${fmtEur(stats.avgWonNet)} je Auftrag`}
                    icon={<TrendingUp className="w-4 h-4" />}
                    tone="emerald"
                    onClick={onSelectStatus ? () => onSelectStatus('accepted') : undefined}
                />
                <Tile
                    caption="Abgelaufen"
                    badge={p}
                    value={fmtEur(stats.expiredNet)}
                    sub={`${stats.expiredCount} ohne Entscheidung verfallen`}
                    icon={<CalendarX className="w-4 h-4" />}
                    tone={stats.expiredCount > 0 ? 'amber' : 'slate'}
                    onClick={onSelectStatus ? () => onSelectStatus('expired') : undefined}
                />
            </div>

            {stats.avgDecisionDays !== null && (
                <p className="text-[11px] text-slate-400 px-1">
                    Ø Entscheidungsdauer im Zeitraum:{' '}
                    <span className="font-bold text-slate-500">{Math.round(stats.avgDecisionDays)} Tage</span> ab Versand
                </p>
            )}

            {/* Eingelesene Altbelege zaehlen ueberall mit — hier steht, wie viel
                der Zahlen oben nachgetragener Bestand ist statt frischer Akquise. */}
            {stats.importedCount > 0 && (
                <p className="text-[11px] text-slate-400 px-1">
                    Davon eingelesen:{' '}
                    <span className="font-bold text-slate-500">{stats.importedCount}</span>
                    {' '}Alt-/Fremdangebot{stats.importedCount === 1 ? '' : 'e'} mit{' '}
                    <span className="font-bold text-slate-500">{fmtEur(stats.importedNet)}</span> netto
                </p>
            )}
        </div>
    );
};
